import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PropertySearchResult {
  id: string;
  name: string;
  address: string | null;
  readiness_state: string | null;
  data_source_mode: string | null;
  org_name: string;
}

export interface PropertyDetailPlant {
  id: string;
  display_name: string;
  provider_type: string;
  provider_plant_id: string | null;
  tier: string | null;
  install_date: string | null;
}

export interface PropertyDetailSystem {
  id: string;
  display_name: string;
  provider_type: string;
  device_sn: string | null;
  is_primary: boolean;
  last_poll_at: string | null;
  last_poll_status: string | null;
}

export interface PropertyDetailHomeowner {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  granted_at: string;
}

export interface PropertyDetailIssue {
  id: string;
  title: string;
  severity: string;
  status: string;
  source: string;
  created_at: string;
}

export interface PropertyDetailVisit {
  id: string;
  visit_type: string;
  status: string;
  scheduled_at: string | null;
  completed_at: string | null;
  technician_name: string | null;
}

export interface PropertyDetailGateway {
  id: string;
  claim_code: string;
  lifecycle_status: string;
  operational_status: string;
  last_heartbeat_at: string | null;
  firmware_version: string | null;
}

export interface PropertyDetailTelemetry {
  last_telemetry_at: string | null;
  online: boolean | null;
  data_stale: boolean | null;
}

export interface PropertyDetailSubscription {
  id: string;
  plan_name: string;
  status: string;
  price_cents: number;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface PropertyDetailQuality {
  data_quality_tier: string | null;
  data_quality_score: number | null;
  quality_reason_codes: string[] | null;
  telemetry_coverage_7d: number | null;
  pv_learning_health: string | null;
  fallback_rate_7d: number | null;
}

export interface PropertyDetailData {
  property: {
    id: string;
    name: string;
    address: string | null;
    timezone: string | null;
    readiness_state: string | null;
    data_source_mode: string | null;
    onboarding_complete: boolean;
    billing_mode: string | null;
    battery_kwh_total: number | null;
    created_at: string;
  };
  owner_org: { id: string; name: string } | null;
  service_provider_org: { id: string; name: string } | null;
  plants: PropertyDetailPlant[];
  energy_systems: PropertyDetailSystem[];
  subscription: PropertyDetailSubscription | null;
  homeowner_users: PropertyDetailHomeowner[];
  recent_issues: PropertyDetailIssue[];
  recent_visits: PropertyDetailVisit[];
  gateway: PropertyDetailGateway | null;
  telemetry: PropertyDetailTelemetry | null;
  data_quality: PropertyDetailQuality | null;
}

async function searchProperties(query: string): Promise<PropertySearchResult[]> {
  const { data, error } = await supabase.rpc('admin_search_properties', { p_query: query });
  if (error) throw error;
  return (data as PropertySearchResult[]) ?? [];
}

async function fetchPropertyDetail(propertyId: string): Promise<PropertyDetailData> {
  const { data, error } = await supabase.rpc('admin_property_detail', { p_property_id: propertyId });
  if (error) throw error;
  return data as PropertyDetailData;
}

export function useAdminSearchProperties(query: string) {
  return useQuery({
    queryKey: ['admin-search-properties', query],
    queryFn: () => searchProperties(query),
    enabled: query.trim().length >= 2,
  });
}

export function useAdminPropertyDetail(propertyId: string | null) {
  return useQuery({
    queryKey: ['admin-property-detail', propertyId],
    queryFn: () => fetchPropertyDetail(propertyId!),
    enabled: !!propertyId,
  });
}
